import { useCallback, useReducer, useEffect } from 'react'
import { IFile, IPageInfo } from '@api/types'
import api from '@api'
import { IImportResult } from '@api/import/upload'
import { IHistory } from '@api/import/history'
import { StageAction, Stages } from '../../ImportFlow'
import track from '@track'
import Event from '@track/Event'

enum Actions {
  LOAD_HISTORY,
  NEXT_HISTORY,
  SET_FILE,
  SET_STAGE,
  SET_RESULT,
  START_LOAD_HISTORY,
  INITIAL_LOAD,
  ClEAR_FIRST_LOAD,
}

interface LoadHistoryAction {
  type: Actions.LOAD_HISTORY
  history: { nodes: IImportResult[]; pageInfo: IPageInfo }
}

interface LoadMoreHistoryAction {
  type: Actions.NEXT_HISTORY
}

interface SetFileAction {
  type: Actions.SET_FILE
  file: IFile | null
}

interface SetStageAction {
  type: Actions.SET_STAGE
  stage: StageAction
}

interface SetResultAction {
  type: Actions.SET_RESULT
  result: IImportResult
}

interface StartLoadHistoryAction {
  type: Actions.START_LOAD_HISTORY
}

interface InitialLoadAction {
  type: Actions.INITIAL_LOAD
}

interface ClearFirstLoad {
  type: Actions.ClEAR_FIRST_LOAD
}

type RootAction =
  | LoadHistoryAction
  | LoadMoreHistoryAction
  | SetFileAction
  | SetStageAction
  | SetResultAction
  | StartLoadHistoryAction
  | InitialLoadAction
  | ClearFirstLoad

interface IState {
  file: IFile | null
  stage: StageAction
  result: IImportResult[]
  history: IHistory
  firstLoad: boolean
}

export enum Import {
  FLOOR = 'FLOOR',
  BUILDING = 'BUILDING',
  OP_COST = 'OP_COST',
  LEASE = 'LEASE',
  EMPLOYEE_ASSETS = 'EMPLOYEE_ASSETS',
  SPACE_ASSETS = 'SPACE_ASSETS',
}

function reducer(state: IState, action: RootAction) {
  switch (action.type) {
    case Actions.LOAD_HISTORY:
      return {
        ...state,
        history: {
          ...state.history,
          results: state.history.isPaging
            ? [...state.history.results, ...action.history.nodes]
            : action.history.nodes,
          isLoading: false,
          isPaging: false,
          page: action.history.pageInfo,
        },
      }
    case Actions.NEXT_HISTORY:
      return {
        ...state,
        history: {
          ...state.history,
          after: state.history.page.endCursor,
          isPaging: true,
          page: {
            ...state.history.page,
          },
        },
      }
    case Actions.SET_FILE:
      return { ...state, file: action.file }
    case Actions.SET_STAGE:
      return { ...state, stage: action.stage }
    case Actions.SET_RESULT:
      return { ...state, result: [action.result] }
    case Actions.START_LOAD_HISTORY:
      return {
        ...state,
        history: {
          ...state.history,
        },
      }
    case Actions.INITIAL_LOAD:
      return {
        ...state,
        firstLoad: false,
      }
    case Actions.ClEAR_FIRST_LOAD:
      return {
        ...state,
        firstLoad: true,
      }
    default:
      return state
  }
}

interface UseImportHook extends IState {
  setFile: (file: IFile) => void
  setStage: (stage: StageAction) => void
  fetchHistory: () => void
  nextHistory: () => void
  upload: () => Promise<IImportResult>
  clear: () => void
}

type ImportMap = { [key in Import]: string }

const historyTypeMap: ImportMap = {
  [Import.BUILDING]: 'buildingImports',
  [Import.FLOOR]: 'floorImports',
  [Import.OP_COST]: 'operationCost',
  [Import.LEASE]: 'leaseImports',
  [Import.EMPLOYEE_ASSETS]: 'employeesTagsImports',
  [Import.SPACE_ASSETS]: 'spacesTagsImports',
}

function getHistoryType(value: Import) {
  return historyTypeMap[value]
}

const uploadTypeMap: ImportMap = {
  [Import.BUILDING]: 'building',
  [Import.FLOOR]: 'floor',
  [Import.OP_COST]: 'operation_cost',
  [Import.LEASE]: 'lease',
  [Import.EMPLOYEE_ASSETS]: 'employees_tags',
  [Import.SPACE_ASSETS]: 'spaces_tags',
}

function getUploadType(value: Import) {
  return uploadTypeMap[value]
}

export default function useImport(type: Import): UseImportHook {
  const [data, dispatch] = useReducer(reducer, {
    firstLoad: true,
    file: null,
    stage: Stages.TEMPLATE_PROVIDER,
    history: {
      after: '',
      results: [],
      isLoading: true,
      isPaging: false,
      page: {
        hasNextPage: false,
        endCursor: '',
      },
    },
    result: [],
  })

  const fetchHistory = useCallback(() => {
    if (data.firstLoad) return
    dispatch({ type: Actions.START_LOAD_HISTORY })
    api.import.history({ after: data.history.after }, getHistoryType(type)).then((history) => {
      dispatch({ type: Actions.LOAD_HISTORY, history })
    })
  }, [data.history.after, type, data.firstLoad])

  useEffect(fetchHistory, [data.history.after, data.firstLoad])

  return {
    ...data,
    setFile: (file: IFile) => dispatch({ type: Actions.SET_FILE, file }),
    setStage: (stage: StageAction) => {
      dispatch({ type: Actions.SET_STAGE, stage })

      if (stage === Stages.COMPLETE) {
        dispatch({ type: Actions.ClEAR_FIRST_LOAD })
      }
    },

    upload: () => {
      return api.import
        .upload({ file: data.file ? data.file.content : '' }, getUploadType(type))
        .then((result) => {
          track.importDialog({
            module: `${getUploadType(type)} import`,
            eventName: Event.IMPORT,
          })
          dispatch({ type: Actions.SET_RESULT, result })
          dispatch({ type: Actions.SET_FILE, file: null })

          return result
        })
    },
    clear: () => {
      dispatch({ type: Actions.SET_STAGE, stage: Stages.TEMPLATE_PROVIDER })
      dispatch({ type: Actions.SET_FILE, file: null })
    },
    fetchHistory: () => {
      track.importDialog({
        module: `${getUploadType(type)} import`,
        eventName: Event.HISTORY,
      })
      dispatch({ type: Actions.INITIAL_LOAD })
    },
    nextHistory: () => dispatch({ type: Actions.NEXT_HISTORY }),
  }
}
